import { createSlice } from "@reduxjs/toolkit";
import { VerifyUser, loginUser } from "../thunk/auth.thunk";

// Function to load data from localStorage
const loadFromLocalStorage = (key) => {
  try {
    const serializedData = localStorage.getItem(key);
    return serializedData ? JSON.parse(serializedData) : null;
  } catch (err) {
    console.error(`Could not load ${key} from localStorage`, err);
    return null;
  }
};

// Function to save data to localStorage
const saveToLocalStorage = (key, data) => {
  try {
    const serializedData = JSON.stringify(data);
    localStorage.setItem(key, serializedData);
  } catch (err) {
    console.error(`Could not save ${key} to localStorage`, err);
  }
};

// Initial state
const initialState = {
  user: loadFromLocalStorage("user") || {},
  isAuthenticated: !!loadFromLocalStorage("accessToken"),
  loading: false,
  error: null,
  accessToken: loadFromLocalStorage("accessToken"),
  tfaRequired: false, // <- new: indicates OTP verification needed
  tfaUserId: null,    // <- temporary userId from login response
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    reset: (state) => {
      state.user = {};
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.accessToken = null;
      state.tfaRequired = false;
      state.tfaUserId = null;
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
    },
    setTfaRequired: (state, action) => {
      state.tfaRequired = action.payload.required;
      state.tfaUserId = action.payload.userId || null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, { payload }) => {
        state.loading = false;

        if (payload.twoFactorRequired) {
          // OTP required: store userId temporarily, do not save JWT yet
          state.user = payload.user || {};
          state.tfaRequired = true;
          state.tfaUserId = payload.userId;
          state.isAuthenticated = false;
          state.accessToken = null;
        } else {
          // Normal login without OTP
          state.user = payload.user;
          state.isAuthenticated = true;
          state.accessToken = payload.token;
          state.tfaRequired = false;
          state.tfaUserId = null;

          saveToLocalStorage("accessToken", payload.token);
          saveToLocalStorage("user", payload.user);
        }
      })
      .addCase(loginUser.rejected, (state, { error }) => {
        state.error = error.message;
        state.isAuthenticated = false;
        state.loading = false;
        state.user = {};
        state.accessToken = null;
        state.tfaRequired = false;
        state.tfaUserId = null;

        localStorage.removeItem("accessToken");
        localStorage.removeItem("user");
      })
      .addCase(VerifyUser.fulfilled, (state, { payload }) => {
        // OTP verification successful
        state.user = payload.user;
        state.accessToken = payload.token;
        state.isAuthenticated = true;
        state.tfaRequired = false;
        state.tfaUserId = null;

        saveToLocalStorage("accessToken", payload.token);
        saveToLocalStorage("user", payload.user);
      })
      .addCase(VerifyUser.rejected, (state, { error }) => {
        state.error = error.message;
      });
  },
});

export const { reset, setTfaRequired } = authSlice.actions;

export default authSlice.reducer;

